/**
 * Translation review rules of a target language chapter of the material editor.
 */

/**
 * A chapter has no translation when it is empty or still holds the source language content.
 *
 * The texts are compared trimmed, the same way the backend compares them when it decides
 * whether the language may be shown to learners.
 */
export const isChapterUntranslated = (chapter, sourceChapter) => {
    const content = (chapter.content ? chapter.content : '').trim();
    if (!content.length) {
        return true;
    }

    return !!sourceChapter && content === (sourceChapter.content ? sourceChapter.content : '').trim();
};

/**
 * A confirmed translation is out of date when the source language content changed after it was
 * reviewed: the backend tells it for the saved content, and an unsaved change of the source in the
 * open editor counts as well. Translations without a source fingerprint (legacy ones) can not go
 * out of date, the same way as in the backend.
 *
 * @param chapter chapter of a target language
 * @param isSourceChanged whether the source chapter was changed in the editor since it was saved
 */
export const isTranslationOutdated = (chapter, isSourceChanged) => {
    if (chapter.isTranslationOutdated === true) {
        return true;
    }

    return !!isSourceChanged
        && chapter.isTranslationConfirmed !== false
        && chapter.isTranslationSourceTracked === true;
};

/**
 * A chapter needs review when it is part of the target language a learner would get and its
 * translation is not a confirmed translation of the current source content.
 *
 * This single definition is used for the remaining count, the Next to review navigation and
 * the automatic advance after a confirmation, so they can never disagree.
 *
 * @param chapter chapter of the selected target language
 * @param sourceChapter the matching source language chapter, if there is one
 * @param isSourceChanged whether the source chapter was changed in the editor since it was saved
 */
export const doesChapterNeedReview = (chapter, sourceChapter, isSourceChanged = false) => {
    if (!chapter || chapter.type !== 'chapter' || chapter.isVisible === false) {
        return false;
    }

    // not translated yet
    if (isChapterUntranslated(chapter, sourceChapter)) {
        return true;
    }

    // waiting for the review of a generated translation
    if (chapter.isTranslationConfirmed === false) {
        return true;
    }

    // confirmed earlier, but the source language content has changed since
    return isTranslationOutdated(chapter, isSourceChanged);
};

/**
 * The translation state of a target language chapter, one of missing / pending / outdated / current.
 */
export const getChapterTranslationState = (chapter, sourceChapter, isSourceChanged = false) => {
    if (isChapterUntranslated(chapter, sourceChapter)) {
        return 'missing';
    }

    if (chapter.isTranslationConfirmed === false) {
        return 'pending';
    }

    return isTranslationOutdated(chapter, isSourceChanged) ? 'outdated' : 'current';
};

/**
 * A generated translation always has to be reviewed, even when the previous one was confirmed,
 * and the earlier confirmation no longer describes the new machine generated content.
 */
export const markChapterTranslationForReview = (chapter) => {
    chapter.isTranslationConfirmed = false;
    chapter.translationConfirmedByName = null;
    chapter.translationConfirmedAt = null;
    // the fresh translation is based on the current source, it only waits for the review
    chapter.isTranslationOutdated = false;
    chapter.isTranslationSourceTracked = false;
};

/**
 * Formats the stored confirmation time as DD.MM.YYYY HH:mm.
 */
export const formatTranslationConfirmedAt = (confirmedAt) => {
    if (!confirmedAt) {
        return null;
    }

    const parts = String(confirmedAt).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!parts) {
        return null;
    }

    return `${parts[3]}.${parts[2]}.${parts[1]} ${parts[4]}:${parts[5]}`;
};

/**
 * Translates a text that names a language. A broken translation string must not break the
 * whole flow, the language is then appended to the untranslated text.
 */
export const translateWithLanguage = ($translate, key, languageLabel) => {
    try {
        return $translate.instant(key, { language: languageLabel });
    }
    catch (error) {
        return `${$translate.instant(key)} (${languageLabel})`;
    }
};



// WEBPACK FOOTER //
// ./src/app/components/modals/courses/authoringTool/editor/translationState.js